import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import type { KeyboardEvent, PointerEvent } from 'react';

type MobileScoreRow = {
  playerName: string;
  gross: number[];
  editableGross: number[];
  absent: boolean;
  disabled: boolean;
  strokes: number[];
  net: number[];
  onChange: (holeIndex: number, value: number) => void;
};

export function MobileHoleScoreEditor({ rows }: { rows: MobileScoreRow[] }) {
  const id = useId();
  const [holeIndexes, setHoleIndexes] = useState<Record<number, number>>({});
  const [activeRow, setActiveRow] = useState<number | null>(null);
  const editorRef = useRef<HTMLDivElement>(null);
  const inputsRef = useRef<Array<HTMLInputElement | null>>([]);
  // Keep the player identity even when Safari blurs the input before a button click.
  const activeRowRef = useRef<number | null>(null);
  const editing = activeRow !== null;
  const holeIndex = holeIndexes[activeRow ?? 0] ?? 0;

  useLayoutEffect(() => {
    if (!editing) return;

    const editor = editorRef.current!;
    const viewport = window.visualViewport;
    const scrollX = window.scrollX;
    const scrollY = window.scrollY;
    const body = document.body;
    const saved = {
      position: body.style.position,
      top: body.style.top,
      left: body.style.left,
      width: body.style.width,
      overflow: body.style.overflow,
    };
    // Freeze the underlying page. The existing focused input stays mounted in
    // this editor while it moves to the top of the keyboard's visible viewport.
    Object.assign(body.style, {
      position: 'fixed', top: `${-scrollY}px`, left: `${-scrollX}px`, width: '100%', overflow: 'hidden',
    });
    const positionEditor = () => {
      editor.style.setProperty('--score-viewport-top', `${viewport?.offsetTop ?? 0}px`);
      editor.style.setProperty('--score-viewport-left', `${viewport?.offsetLeft ?? 0}px`);
      editor.style.setProperty('--score-viewport-width', `${viewport?.width ?? window.innerWidth}px`);
      editor.style.setProperty('--score-viewport-height', `${viewport?.height ?? window.innerHeight}px`);
    };
    const onResize = () => {
      if (window.innerWidth > 640) {
        if (activeRowRef.current !== null) inputsRef.current[activeRowRef.current]?.blur();
        setActiveRow(null);
      } else positionEditor();
    };
    positionEditor();
    viewport?.addEventListener('resize', positionEditor);
    viewport?.addEventListener('scroll', positionEditor);
    window.addEventListener('resize', onResize);
    return () => {
      viewport?.removeEventListener('resize', positionEditor);
      viewport?.removeEventListener('scroll', positionEditor);
      window.removeEventListener('resize', onResize);
      Object.assign(body.style, saved);
      const scrollBehavior = document.documentElement.style.scrollBehavior;
      document.documentElement.style.scrollBehavior = 'auto';
      window.scrollTo(scrollX, scrollY);
      document.documentElement.style.scrollBehavior = scrollBehavior;
    };
  }, [editing]);

  const activeDisabled = activeRow !== null && (!rows[activeRow] || rows[activeRow]!.disabled || rows[activeRow]!.absent);
  useEffect(() => {
    if (activeDisabled) setActiveRow(null);
  }, [activeDisabled]);

  const focusPlayer = () => {
    const index = activeRowRef.current;
    if (index !== null) inputsRef.current[index]?.focus({ preventScroll: true });
  };
  const moveHole = (direction: number) => {
    const index = activeRowRef.current;
    if (index === null) return;
    setHoleIndexes((current) => ({
      ...current,
      [index]: Math.max(0, Math.min(8, (current[index] ?? 0) + direction)),
    }));
    // Synchronous focus in the user gesture keeps/reopens the iOS keyboard.
    // Do not depend on document.activeElement or defer this to an effect.
    focusPlayer();
  };
  const keepPlayerFocus = (event: PointerEvent<HTMLButtonElement>) => {
    event.preventDefault();
    focusPlayer();
  };
  const finishEditing = () => {
    if (activeRowRef.current !== null) inputsRef.current[activeRowRef.current]?.blur();
    setActiveRow(null);
  };
  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (!editing) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      finishEditing();
    } else if (event.key === 'Tab') {
      const controls = Array.from(editorRef.current!.querySelectorAll<HTMLElement>('button:not(:disabled), input:not(:disabled)'))
        .filter((control) => !control.closest('[hidden]'));
      const first = controls[0];
      const last = controls.at(-1);
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last?.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first?.focus();
      }
    }
  };

  return (
    <div
      ref={editorRef}
      className={`mobile-score-editor${editing ? ' mobile-score-editor-active' : ''}`}
      role={editing ? 'dialog' : undefined}
      aria-modal={editing ? true : undefined}
      aria-label={editing ? `${rows[activeRow]!.playerName} score entry` : 'Hole score entry'}
      onKeyDown={handleKeyDown}
    >
      <div className="mobile-score-heading">
        <strong>{editing ? 'Enter scores' : 'Tap a score to enter holes'}</strong>
        <button className="secondary" type="button" hidden={!editing} onClick={finishEditing}>Done</button>
      </div>
      <div className="mobile-score-rows">
        {rows.map((row, rowIndex) => {
          const rowHole = holeIndexes[rowIndex] ?? 0;
          return (
            <div className="mobile-score-row" key={rowIndex} hidden={editing && activeRow !== rowIndex}>
              <label htmlFor={`${id}-score-${rowIndex}`}>{row.playerName} Gross</label>
              <input
                ref={(input) => { inputsRef.current[rowIndex] = input; }}
                id={`${id}-score-${rowIndex}`}
                aria-describedby={`${id}-hole-${rowIndex}`}
                type="number"
                inputMode="numeric"
                enterKeyHint={rowHole === 8 ? 'done' : 'next'}
                min={1}
                max={20}
                step={1}
                disabled={row.disabled || row.absent}
                value={row.absent ? row.gross[rowHole] ?? '' : Number.isFinite(row.editableGross[rowHole]!) ? row.editableGross[rowHole]! : ''}
                onFocus={() => {
                  if (window.innerWidth > 640) return;
                  activeRowRef.current = rowIndex;
                  setActiveRow(rowIndex);
                }}
                onChange={(event) => row.onChange(rowHole, event.target.value === '' ? Number.NaN : Number(event.target.value))}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    if (rowHole === 8) finishEditing();
                    else moveHole(1);
                  }
                }}
              />
              <span className="mobile-score-detail" id={`${id}-hole-${rowIndex}`}>
                Hole {rowHole + 1} · Strokes {row.strokes[rowHole] ?? 0} · Net {Number.isFinite(row.net[rowHole]!) ? row.net[rowHole] : '–'}
              </span>
            </div>
          );
        })}
      </div>
      <div className="mobile-score-navigation" hidden={!editing}>
        <button className="secondary" type="button" disabled={holeIndex === 0}
          onPointerDown={keepPlayerFocus} onMouseDown={(event) => event.preventDefault()} onClick={() => moveHole(-1)}>
          Previous
        </button>
        <strong aria-live="polite">Hole {holeIndex + 1} of 9</strong>
        <button className="secondary" type="button" disabled={holeIndex === 8}
          onPointerDown={keepPlayerFocus} onMouseDown={(event) => event.preventDefault()} onClick={() => moveHole(1)}>
          Next
        </button>
      </div>
      <p className="mobile-score-hint" hidden={!editing}>Done returns to the scorecard. Save scores there when finished.</p>
    </div>
  );
}
